import React, { useEffect, useMemo, useState } from 'react';
import { amActions } from '../amStore';
import { COLORS, CO2_BASELINE, CO2_GROUPS } from '../amConfig';
import { monthLabel } from '../amRules';
import { co2Group, co2Stats, groupBy, sortedMonths, pct } from '../amMetrics';
import {
  Panel, Seg, KpiCard, Chart, clickTo, SortTable, fmtInt, fmtNum, fmtPct,
} from '../amUi';

const GROUPS = Object.keys(CO2_BASELINE);
// CO2 page palette (from the approved prototype): navy for Legrand group /
// primary, gold for Numeric / secondary. Used on this page only.
const CO2_NAVY = '#2E4A6A';
const CO2_GOLD = '#C9A227';
const CO2_PALETTE = [CO2_NAVY, CO2_GOLD, '#5F7D9C', '#E0C468', '#93A9C0', '#8A6F12', '#B9C8D8'];
const GROUP_COLORS = { 'Legrand group': CO2_NAVY, Numeric: CO2_GOLD };
const t = (kg) => kg / 1000;
const good = (v) => (v === null ? undefined : v >= 0 ? COLORS.green : COLORS.red);

function GroupStrip({ g, fullYear, s, members }) {
  const active = s.filters.entity.length === members.length && members.every((e) => s.filters.entity.includes(e));
  const click = () => amActions.toggleSet('entity', members);
  const hint = `Filter the dashboard to ${members.join(', ')}`;
  const kp = { onClick: click, active, hint };
  return (
    <div className="am-co2-strip">
      <div className="am-co2-name" style={{ borderColor: GROUP_COLORS[g.group] }}>
        <b>{g.group}</b>
        <span>Baseline 2024: {fmtInt(s.co2Cfg.base[g.group].grossT)} t · {fmtNum(g.baselineTpt)}% TCO₂/ton</span>
      </div>
      <div className="am-kpis seven compact">
        <KpiCard {...kp} label="Actual CO₂" unit="t" value={g.actual === null ? 'Needs distance' : fmtInt(g.actual)}
          sub={g.actual === null ? undefined : `${fmtInt(g.weightT)} t moved, ${fmtInt(g.shipments)} shipments`} />
        <KpiCard {...kp} label={fullYear ? 'Baseline CO₂ (full year)' : 'Baseline CO₂ (pro-rated)'} unit="t" value={fmtInt(g.baseline)} />
        <KpiCard {...kp} label="TCO₂/ton" unit="%" value={fmtPct(g.tpt)} sub={`Baseline ${fmtNum(g.baselineTpt)}%`} />
        <KpiCard {...kp} label="CO₂ reduction" unit="%" value={fmtPct(g.reduction)} accent={good(g.reduction)} />
        <KpiCard {...kp} label="Final CO₂ improvement" unit="%" value={fmtPct(g.finalImprovement, 2)} accent={good(g.finalImprovement)} sub="Reduction × 0.5" />
        <KpiCard {...kp} label="TCO₂/ton improvement" unit="%" value={fmtPct(g.tptImprovement)} accent={good(g.tptImprovement)} />
        <KpiCard {...kp} label="Final KPI 2" unit="%" value={fmtPct(g.finalKpi2, 2)} accent={good(g.finalKpi2)} sub="Improvement × 0.1" />
      </div>
    </div>
  );
}

const EF_MODES = ['Road', 'Rail', 'EV', 'Air', 'CNG'];

/** Prototype's "Calculation settings": editable factors, saved in this browser. */
function Co2Settings({ cfg }) {
  const [draft, setDraft] = useState(() => JSON.stringify(cfg));
  useEffect(() => { setDraft(JSON.stringify(cfg)); }, [cfg]);
  const set = (path, raw) => {
    const v = parseFloat(raw);
    if (!(v >= 0)) return; // ignore blanks / negatives, keep the last valid value
    const next = JSON.parse(JSON.stringify(cfg));
    let o = next;
    for (let i = 0; i < path.length - 1; i += 1) o = o[path[i]];
    o[path[path.length - 1]] = v;
    amActions.setCo2Cfg(next);
  };
  const field = (label, path, value, step = 'any') => (
    <label key={path.join('.')} className="am-cfg-row">
      <span>{label}</span>
      <input key={`${draft}-${path.join('.')}`} className="am-cfg-input" type="number" min="0" step={step} defaultValue={value}
        onBlur={(e) => set(path, e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} />
    </label>
  );
  const groups = Object.keys(cfg.base);
  return (
    <Panel title="Calculation settings" subtitle="Emission factors per tonne-km (÷ 10,00,000) and CO₂ parameters. Saved in this browser; changes recalculate every CO₂ figure.">
      <div className="am-cfg">
        <div className="am-cfg-col">
          <h4 className="am-sub">Emission factors</h4>
          {EF_MODES.map((m) => field(m, ['ef', m], cfg.ef[m]))}
        </div>
        <div className="am-cfg-col">
          <h4 className="am-sub">Distance</h4>
          {field('Road circuity factor (road km ÷ straight-line km)', ['roadFactor'], cfg.roadFactor, '0.05')}
          {field('Minimum distance per shipment (km)', ['minKm'], cfg.minKm, '1')}
        </div>
        <div className="am-cfg-col">
          <h4 className="am-sub">2024 baseline</h4>
          {groups.map((g) => (
            <React.Fragment key={g}>
              {field(`Baseline TCO₂/ton %, ${g}`, ['base', g, 'tco2PerTon'], cfg.base[g].tco2PerTon, '0.1')}
              {field(`Baseline gross CO₂ (t), ${g}`, ['base', g, 'grossT'], cfg.base[g].grossT, '1')}
            </React.Fragment>
          ))}
        </div>
      </div>
      <p className="am-note">Fuel mapping: Air mode → Air; Rail mode or fuel → Rail; fuel EV → EV; fuel CNG → CNG; diesel or blank → Road. Air uses straight-line distance.</p>
      <button type="button" className="am-btn ghost sm" onClick={amActions.resetCo2Cfg}>Reset to reference values</button>
    </Panel>
  );
}

export default function Co2Page({ ctx }) {
  const { s, rows } = ctx;
  const fullYear = s.co2FullYear;
  const cfg = s.co2Cfg;
  const base = cfg.base;
  const scope = useMemo(() => rows.filter((r) => r.co2Group), [rows]);
  const months = useMemo(() => sortedMonths(rows), [rows]);
  const groups = useMemo(() => GROUPS.map((g) => co2Group(rows, g, months.length, fullYear, base[g])), [rows, months.length, fullYear, base]);
  // Entity values exactly as they appear in the data (e.g. "LEGRAND"), per group.
  const groupEntities = useMemo(() => {
    const out = {};
    s.rows.forEach((r) => { if (r.co2Group) (out[r.co2Group] = out[r.co2Group] || new Set()).add(r.entity); });
    return Object.fromEntries(Object.entries(out).map(([k, v]) => [k, [...v].sort()]));
  }, [s.rows]);
  const noDistance = useMemo(() => scope.length > 0 && !scope.some((r) => r.distKm !== null), [scope]);
  const missingKm = useMemo(() => scope.filter((r) => r.distKm === null && r.weight !== null).length, [scope]);

  const byGroupMonth = useMemo(() => {
    const m = groupBy(scope, (r) => `${r.co2Group}|${r.month}`);
    return Object.fromEntries(GROUPS.map((g) => [g, months.map((mo) => co2Stats(m.get(`${g}|${mo}`) || []))]));
  }, [scope, months]);

  const monthlyCo2 = useMemo(() => ({
    chart: { type: 'column' },
    xAxis: { categories: months.map(monthLabel) },
    yAxis: { title: { text: 'CO₂ (t)' } },
    plotOptions: { series: clickTo((cat, pt) => amActions.toggle('month', months[pt.index])) },
    tooltip: { shared: true, valueDecimals: 1, valueSuffix: ' t' },
    series: GROUPS.flatMap((g) => [
      { type: 'column', name: `${g} actual`, color: GROUP_COLORS[g], data: byGroupMonth[g].map((x) => +x.co2.toFixed(1)) },
      { type: 'line', name: `${g} baseline (÷12)`, color: GROUP_COLORS[g], dashStyle: 'Dash', marker: { enabled: false }, data: months.map(() => +(base[g].grossT / 12).toFixed(1)) },
    ]),
  }), [byGroupMonth, months, base]);

  const monthlyTpt = useMemo(() => ({
    chart: { type: 'line' },
    xAxis: { categories: months.map(monthLabel) },
    yAxis: { title: { text: 'TCO₂/ton (%)' }, labels: { format: '{value}%' } },
    plotOptions: { series: clickTo((cat, pt) => amActions.toggle('month', months[pt.index])) },
    tooltip: { shared: true, valueDecimals: 2, valueSuffix: '%' },
    series: GROUPS.flatMap((g) => [
      { name: `${g}`, color: GROUP_COLORS[g], data: byGroupMonth[g].map((x) => (x.tpt === null ? null : +x.tpt.toFixed(2))), marker: { radius: 4 } },
      { name: `${g} baseline`, color: GROUP_COLORS[g], dashStyle: 'Dash', marker: { enabled: false }, data: months.map(() => base[g].tco2PerTon) },
    ]),
  }), [byGroupMonth, months, base]);

  const movement = useMemo(() => {
    const total = co2Stats(scope).co2;
    return [...groupBy(scope, 'primSec').entries()].map(([name, items]) => {
      const x = co2Stats(items);
      return { name, co2: x.co2, share: pct(x.co2, total), weightT: x.weightT, tpt: x.tpt };
    });
  }, [scope]);

  const primaryWeight = useMemo(() => {
    const g = groupBy(rows.filter((r) => r.primSec === 'Primary'), 'month');
    return {
      chart: { type: 'column' },
      xAxis: { categories: months.map(monthLabel) },
      yAxis: { title: { text: 'Weight (t)' } },
      legend: { enabled: false },
      plotOptions: { series: clickTo((cat, pt) => { amActions.toggle('month', months[pt.index]); }) },
      tooltip: { valueDecimals: 1, valueSuffix: ' t' },
      series: [{ name: 'Primary weight', color: CO2_NAVY, data: months.map((m) => +t((g.get(m) || []).reduce((a, r) => a + (r.weight || 0), 0)).toFixed(1)), dataLabels: { enabled: true, format: '{y:,.0f}', style: { fontSize: '10px' } } }],
    };
  }, [rows, months]);

  const entityWeight = useMemo(() => {
    const entities = [...groupBy(rows, 'entity').entries()].sort((a, b) => b[1].length - a[1].length).map((e) => e[0]);
    const g = groupBy(rows, (r) => `${r.entity}|${r.month}`);
    return {
      chart: { type: 'column' },
      xAxis: { categories: months.map(monthLabel) },
      yAxis: { title: { text: 'Weight (t)' } },
      plotOptions: {
        column: { stacking: 'normal' },
        series: { ...clickTo((cat, pt) => amActions.toggle('entity', pt.series.name)), events: { legendItemClick() { amActions.toggle('entity', this.name); return false; } } },
      },
      tooltip: { shared: true, valueDecimals: 1, valueSuffix: ' t' },
      series: entities.map((e, i) => ({ name: e, color: CO2_PALETTE[i % CO2_PALETTE.length], data: months.map((m) => +t((g.get(`${e}|${m}`) || []).reduce((a, r) => a + (r.weight || 0), 0)).toFixed(1)) })),
    };
  }, [rows, months]);

  // Lane x month heatmap, top 15 lanes by CO2.
  const heat = useMemo(() => {
    const lanes = [...groupBy(scope, 'lane').entries()].map(([lane, items]) => ({ lane, items, co2: co2Stats(items).co2 }))
      .sort((a, b) => b.co2 - a.co2).slice(0, 15);
    const cells = lanes.map((l) => {
      const g = groupBy(l.items, 'month');
      return { lane: l.lane, total: l.co2, values: months.map((m) => co2Stats(g.get(m) || []).co2) };
    });
    const max = Math.max(1e-9, ...cells.flatMap((c) => c.values));
    return { cells, max };
  }, [scope, months]);

  // Entity table by month.
  const entityTable = useMemo(() => {
    const out = [];
    const cum = {};
    months.forEach((m) => {
      GROUPS.forEach((g) => {
        const items = scope.filter((r) => r.month === m && r.co2Group === g);
        const x = co2Stats(items);
        const gBase = base[g];
        const monthlyBase = gBase.grossT / 12;
        const red = x.weightT ? monthlyBase - x.co2 : null;
        if (red !== null) cum[g] = (cum[g] || 0) + red;
        const tptRed = x.tpt !== null ? pct(gBase.tco2PerTon - x.tpt, gBase.tco2PerTon) : null;
        out.push({ key: `${m}-${g}`, month: m, name: g, total: true, weightT: x.weightT, co2: x.co2, tpt: x.tpt,
          red, redPct: red === null ? null : pct(red, monthlyBase), tptRed,
          finalRed: red === null ? null : pct(red, monthlyBase) * 0.5, cum: red === null ? null : cum[g] });
        const members = groupEntities[g] || [];
        if (Object.values(CO2_GROUPS).filter((x) => x === g).length > 1) {
          members.forEach((e) => {
            const y = co2Stats(items.filter((r) => r.entity === e));
            if (!y.weightT) return;
            out.push({ key: `${m}-${g}-${e}`, month: m, name: e, child: true, weightT: y.weightT, co2: y.co2, tpt: y.tpt,
              tptRed: y.tpt !== null ? pct(gBase.tco2PerTon - y.tpt, gBase.tco2PerTon) : null });
          });
        }
      });
    });
    return out;
  }, [scope, months, groupEntities, base]);

  const distanceRows = useMemo(() => [...groupBy(scope, 'lane').entries()].map(([lane, items]) => {
    const withKm = items.filter((r) => r.distKm !== null);
    const x = co2Stats(items);
    return {
      name: lane, rows: items.length, weightT: x.weightT, co2: x.co2,
      km: withKm.length ? withKm.reduce((a, r) => a + r.distKm, 0) / withKm.length : null,
      missing: items.length - withKm.length,
    };
  }).sort((a, b) => b.co2 - a.co2).slice(0, 20), [scope]);

  return (
    <>
      {noDistance && (
        <div className="am-banner">
          <b>CO₂ needs distances, and none matched yet.</b>{' '}
          {!s.lanes && 'The server did not report lane-distance status. Restart Node with the latest autometricsController.js.'}
          {s.lanes && s.lanes.pending && 'The ML emission feed is still loading on the server. Press Refresh in a minute.'}
          {s.lanes && !s.lanes.pending && s.lanes.error && <>The ML emission feed could not be read: <code>{s.lanes.error}</code>.</>}
          {s.lanes && s.lanes.gcError && <>The sign-off distance table was not loaded: <code>{s.lanes.gcError}</code>. Deploy <code>legrand_node/data/autometrics_distances.json</code> and restart Node. </>}
          {s.lanes && !s.lanes.gcError && typeof s.lanes.gcMatched === 'number' && <>{fmtInt(s.lanes.gcMatched)} shipments matched the sign-off distance table ({fmtInt(s.lanes.gcLanes)} lanes loaded). </>}
          {s.lanes && s.lanes.ok && (
            <>
              The ML emission feed returned {fmtInt(s.lanes.feedRows)} rows, giving {fmtInt(s.lanes.lr)} LR, {fmtInt(s.lanes.pincodePairs)} pincode-pair
              and {fmtInt(s.lanes.cityPairs)} city-pair distances, but {fmtInt(s.lanes.matched)} shipments matched.
              {s.lanes.fields && s.lanes.fields.length > 0 && <> Feed fields: <code>{s.lanes.fields.join(', ')}</code></>}
            </>
          )}
        </div>
      )}
      <Panel title="CO₂ against the 2024 baseline"
        subtitle={fullYear ? 'Compared with the full-year baseline' : `Baseline pro-rated to the ${months.length} month${months.length === 1 ? '' : 's'} in view (baseline × months ÷ 12)`}
        actions={<Seg size="sm" value={fullYear ? 'full' : 'pro'} onChange={(v) => amActions.setCo2FullYear(v === 'full')} options={[{ value: 'pro', label: 'Pro-rated' }, { value: 'full', label: 'Full year' }]} />}>
        {groups.map((g) => <GroupStrip key={g.group} g={g} fullYear={fullYear} s={s} members={groupEntities[g.group] || []} />)}
        <p className="am-note"><b>Distances are tentative.</b> Great-circle km between pincode areas (from the approved sign-off model, by LR or by origin → destination lane) × {cfg.roadFactor} road factor, minimum {cfg.minKm} km; lanes not in it use the TMS lane distance. Treat absolute tonnes as indicative and month-to-month direction as the more reliable signal. Netrack and Adlec are out of CO₂ scope.</p>
      </Panel>

      <div className="am-grid two">
        <Panel title="Monthly CO₂ by group" subtitle="Actual vs monthly baseline (annual ÷ 12)"><Chart options={monthlyCo2} height={280} /></Panel>
        <Panel title="Monthly TCO₂/ton by group" subtitle="Dashed lines = 2024 baseline"><Chart options={monthlyTpt} height={280} /></Panel>
      </div>

      <div className="am-grid two">
        <Panel title="Footprint by movement" subtitle="Primary = plant / warehouse to plant / warehouse · click to filter">
          <SortTable rows={movement} defaultSort={{ key: 'co2', dir: 'desc' }} onRowClick={(r) => amActions.toggle('primSec', r.name)}
            columns={[
              { key: 'name', label: 'Movement', render: (r) => <span className="am-name">{r.name}</span> },
              { key: 'co2', label: 'CO₂ (t)', align: 'right', bar: true, render: (r) => fmtNum(r.co2) },
              { key: 'share', label: 'Contribution %', align: 'right', render: (r) => fmtPct(r.share) },
              { key: 'weightT', label: 'Weight (t)', align: 'right', render: (r) => fmtInt(r.weightT) },
              { key: 'tpt', label: 'TCO₂/ton (%)', align: 'right', render: (r) => fmtPct(r.tpt, 2) },
            ]} />
        </Panel>
        <Panel title="Plant → warehouse (primary) weight by month"><Chart options={primaryWeight} height={240} /></Panel>
      </div>

      <Panel title="Lane × month CO₂" subtitle="Top 15 lanes by CO₂ (t) · click a lane to filter">
        <div className="am-table-wrap">
          <table className="am-table am-heat">
            <thead><tr><th>Lane</th>{months.map((m) => <th key={m} className="r">{monthLabel(m)}</th>)}<th className="r">Total (t)</th></tr></thead>
            <tbody>
              {heat.cells.length === 0 && <tr><td colSpan={months.length + 2} className="am-empty">Needs distance</td></tr>}
              {heat.cells.map((c) => (
                <tr key={c.lane} className="clickable" onClick={() => amActions.toggle('lane', c.lane)}>
                  <td className="am-name">{c.lane}</td>
                  {c.values.map((v, i) => (
                    <td key={months[i]} className="r" style={{ background: `rgba(46,74,106,${(0.06 + 0.8 * (v / heat.max)).toFixed(2)})`, color: v / heat.max > 0.5 ? '#fff' : undefined }}>{fmtNum(v)}</td>
                  ))}
                  <td className="r"><b>{fmtNum(c.total)}</b></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="Monthly dispatched weight by entity" subtitle="All entities, including those outside CO₂ scope · click a legend item to filter">
        <Chart options={entityWeight} height={270} />
      </Panel>

      <Panel title="Entity emissions by month" subtitle="Absolute reduction shown only for rows with their own baseline (Legrand group total, Numeric)">
        <div className="am-table-wrap">
          <table className="am-table">
            <thead>
              <tr>
                <th>Month</th><th>Entity</th><th className="r">Weight (t)</th><th className="r">Emission (t)</th><th className="r">TCO₂/ton (%)</th>
                <th className="r">Emission reduction (t)</th><th className="r">Emission reduction %</th><th className="r">TCO₂/ton reduction %</th>
                <th className="r">Final reduction (× 0.5)</th><th className="r">Cumulative reduction (t)</th>
              </tr>
            </thead>
            <tbody>
              {entityTable.map((r) => (
                <tr key={r.key} className={r.total ? 'am-row-total' : 'am-row-child'}>
                  <td>{r.total && r.name === GROUPS[0] ? monthLabel(r.month) : ''}</td>
                  <td>{r.child ? <span className="am-indent">{r.name}</span> : <b>{r.name}</b>}</td>
                  <td className="r">{fmtInt(r.weightT)}</td>
                  <td className="r">{r.weightT ? fmtNum(r.co2) : <span className="muted">Needs distance</span>}</td>
                  <td className="r">{fmtPct(r.tpt, 2)}</td>
                  <td className="r" style={{ color: r.total ? good(r.red) : undefined }}>{r.total ? fmtNum(r.red) : ''}</td>
                  <td className="r">{r.total ? fmtPct(r.redPct) : ''}</td>
                  <td className="r" style={{ color: good(r.tptRed) }}>{fmtPct(r.tptRed)}</td>
                  <td className="r">{r.total ? fmtPct(r.finalRed, 2) : ''}</td>
                  <td className="r">{r.total ? fmtNum(r.cum) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="Distance assumptions" subtitle={`Top 20 lanes by CO₂ · distances from the TMS lane data (ML emission feed)${missingKm ? ` · ${fmtInt(missingKm)} shipments still need a distance` : ''}`}>
        <SortTable rows={distanceRows} defaultSort={{ key: 'co2', dir: 'desc' }} onRowClick={(r) => amActions.toggle('lane', r.name)}
          columns={[
            { key: 'name', label: 'Lane', render: (r) => <span className="am-name">{r.name}</span> },
            { key: 'km', label: 'Distance (km)', align: 'right', render: (r) => (r.km === null ? <span className="muted">Needs distance</span> : fmtInt(r.km)) },
            { key: 'rows', label: 'Shipments', align: 'right', render: (r) => fmtInt(r.rows) },
            { key: 'weightT', label: 'Weight (t)', align: 'right', render: (r) => fmtNum(r.weightT) },
            { key: 'co2', label: 'CO₂ (t)', align: 'right', bar: true, render: (r) => fmtNum(r.co2) },
            { key: 'missing', label: 'Rows needing distance', align: 'right', render: (r) => (r.missing ? fmtInt(r.missing) : '–') },
          ]} />
      </Panel>
      <Co2Settings cfg={cfg} />
    </>
  );
}

